//@ts-check
import { useMemo } from 'react';

import azSortByProperty from '../../../utils/azSortByProperty';
import useCcAppsById from './useCcAppsById';
import useCcEntitlements from './useCcEntitlements';

export const ITEMS_PER_PAGE = 50;

/** @type { import('@action-types/ccApp').SortOption[]} */
export const sortOptions = [
  {
    key: 'mostrecent',
    name: 'Most recent',
    sortFn: (a, b) => {
      const aCreated = a?.entitlement?.created?.on;
      const bCreated = b?.entitlement?.created?.on;
      if (!aCreated || !bCreated) {
        return 0;
      }
      return new Date(bCreated).getTime() - new Date(aCreated).getTime();
    },
  },
  { key: 'title', name: 'Title (A-Z)', sortFn: azSortByProperty('title') },
  { key: 'publisher', name: 'Publisher (A-Z)', sortFn: azSortByProperty('publisherName') },
];

/** @param {import('@action-types/ccApp').UseManageCcAppsOptions} [options] */
const useManageCcApps = ({ sortType, page, ...options } = {}) => {
  const ccEntitlementsQuery = useCcEntitlements(options);
  const entitlements = ccEntitlementsQuery.data?.entitlements?.slice(
    ITEMS_PER_PAGE * (page - 1),
    ITEMS_PER_PAGE * page
  );
  const appIds = entitlements?.map((entitlement) => entitlement?.appId);
  const ccAppsQuery = useCcAppsById({ appIds, ...options });
  const isLoading = ccEntitlementsQuery.isLoading || (ccAppsQuery.isLoading && ccAppsQuery.isFetching);
  const error = ccEntitlementsQuery.error || ccAppsQuery.error;

  const ccApps = useMemo(() => {
    if (ccAppsQuery.data?.apps?.length) {
      const ccApps = ccAppsQuery.data.apps.map((app) => {
        const entitlement = ccEntitlementsQuery.data?.entitlements?.find(
          (entitlement) => entitlement?.appId === app?.appNumber
        );
        return {
          ...(app ?? {}),
          entitlement,
        };
      });
      const sortData = sortOptions.find((option) => option.key === sortType);
      if (sortData) {
        ccApps.sort(sortData.sortFn);
      }
      return ccApps;
    }
  }, [ccEntitlementsQuery.data?.entitlements, ccAppsQuery.data?.apps, sortType]);

  return {
    totalApps: ccEntitlementsQuery.data?.entitlements?.length,
    ccEntitlementsQuery,
    ccAppsQuery,
    isLoading,
    ccApps,
    error,
  };
};

export default useManageCcApps;
